import {
  BucketName,
  BusinessMerchantServiceChargeFee,
} from "../../../../../interfaces/businessFull.interface";
import { DEFAULT_AMEX_BUCKET } from "../../../../common/constants/defaultAmexBucket";
import {
  ONLINE_PRICING_BUCKET_KEYS,
  OnlinePricingBucketKey,
} from "../../../utils/onlinePricingSyncErrors";
import { OnlineGroupKey } from "../../../../../interfaces/onlinePricing.interface";

export type WithInPersonAmexFeeOptions = {
  // When true, drop in-person Amex from the list and never inject a default.
  hideInPersonAmex?: boolean;
};

const isInPersonAmex = (fee: BusinessMerchantServiceChargeFee): boolean =>
  fee.name === BucketName.InPersonAmex;

// PC-757 / PC-926: when visible, ensure a Percentage Amex row (canonical 3.3%
// default if missing/legacy Fixed). When hidden, omit Amex entirely.
export const withInPersonAmexFee = (
  fees?: BusinessMerchantServiceChargeFee[],
  options: WithInPersonAmexFeeOptions = {}
): BusinessMerchantServiceChargeFee[] => {
  const list = fees ? [...fees] : [];
  if (options.hideInPersonAmex) {
    return list.filter((fee) => !isInPersonAmex(fee));
  }
  const defaultAmexFee: BusinessMerchantServiceChargeFee = {
    name: BucketName.InPersonAmex,
    value: DEFAULT_AMEX_BUCKET.value,
    valueType: "Percentage",
  };
  const amexIndex = list.findIndex(isInPersonAmex);
  if (amexIndex === -1) {
    list.push(defaultAmexFee);
  } else if (list[amexIndex].valueType !== "Percentage") {
    list[amexIndex] = defaultAmexFee;
  }
  return list;
};

// Keep any previously stored Amex when the UI hid it, so a save does not wipe
// or invent an Amex fee from client defaults.
export const reconcileInPersonFeesForSave = (
  editedFees: BusinessMerchantServiceChargeFee[] | undefined,
  storedFees: BusinessMerchantServiceChargeFee[] | undefined,
  hideInPersonAmex: boolean
): BusinessMerchantServiceChargeFee[] => {
  if (!hideInPersonAmex) {
    return withInPersonAmexFee(editedFees);
  }
  const withoutAmex = (editedFees ?? []).filter((fee) => !isInPersonAmex(fee));
  const storedAmex = (storedFees ?? []).filter(isInPersonAmex);
  return [...withoutAmex, ...storedAmex];
};

export type OnlineBucketValues = Partial<
  Record<OnlineGroupKey, { value?: number } | undefined>
>;

// PC-819: which online buckets the user actually moved off the stored value.
// The save only sends onlinePricing when this is non-empty, so the partner-band
// hard block must key off the same list — otherwise the form can refuse to
// submit over a value it was never going to send.
//
// `keys` defaults to the four legacy buckets; a 16-bucket merchant passes the
// groups its template pair prices instead. Comparing over the wrong key set
// would either miss an edit or report one the save never carries — and, for a
// 16-bucket merchant, the stored-but-inert groups would always look changed.
export const changedOnlineBucketKeys = <T extends OnlineGroupKey>(
  edited?: OnlineBucketValues,
  stored?: OnlineBucketValues,
  keys: readonly T[] = ONLINE_PRICING_BUCKET_KEYS as readonly OnlinePricingBucketKey[] as readonly T[]
): T[] => keys.filter((key) => edited?.[key]?.value !== stored?.[key]?.value);
